/**
 * The audit trail. Cursor-paginated, newest first, and never editable — 0003 installs
 * DO INSTEAD NOTHING rewrite rules on this table, so there is nothing to offer.
 */
import { useState, type ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { get } from "../lib/api";
import { stamp } from "../lib/format";
import type { AuditEntry, AuditPage, UsersResponse } from "../lib/types";
import { usePermission } from "../hooks/useSession";
import { Shell, Sep } from "../components/Shell";
import { DataTable } from "../components/DataTable";
import { Button, Empty, Failed, Loading, Panel } from "../components/ui";

const PAGE_SIZE = 50;

export function Audit(): ReactElement {
  const can = usePermission();
  // A stack, so "older" and "newer" are both one click and neither refetches from zero.
  const [cursors, setCursors] = useState<string[]>([]);
  const [action, setAction] = useState("");

  const cursor = cursors[cursors.length - 1];
  const query = new URLSearchParams({ limit: String(PAGE_SIZE) });
  if (cursor) query.set("cursor", cursor);
  if (action.trim()) query.set("action", action.trim());

  const page = useQuery({
    queryKey: ["audit", cursor ?? "", action.trim()],
    queryFn: () => get<AuditPage>(`/v1/audit?${query.toString()}`),
  });

  // Only when the caller may read it; an audit:read-only role would get a 403 and this
  // is a nicety, not the screen.
  const users = useQuery({
    queryKey: ["users"],
    queryFn: () => get<UsersResponse>("/v1/users"),
    enabled: can("users:manage"),
  });

  const nameFor = (actor: string): string => {
    const id = actor.startsWith("user:") ? actor.slice(5) : null;
    if (!id) return actor;
    // A deleted user's actions must stay legible: fall back to the raw string.
    return users.data?.users.find((user) => user.id === id)?.display_name ?? actor;
  };

  return (
    <Shell crumb={<>settings <Sep /> audit</>} fill>
      <Panel
        title="Audit"
        fill
        actions={
          <>
            <input
              aria-label="filter by action"
              value={action}
              onChange={(event) => {
                setCursors([]);
                setAction(event.target.value);
              }}
              placeholder="action, e.g. user.invited"
              className="border border-hair bg-ground px-2 py-1 font-mono text-[11px] outline-none focus:border-gold"
            />
            <Button disabled={cursors.length === 0} onClick={() => setCursors((c) => c.slice(0, -1))}>
              newer
            </Button>
            <Button
              disabled={!page.data?.next_cursor}
              onClick={() =>
                setCursors((c) =>
                  page.data?.next_cursor ? [...c, page.data.next_cursor] : c,
                )
              }
            >
              older
            </Button>
          </>
        }
      >
        {page.isPending ? (
          <Loading what="the audit trail" />
        ) : page.error ? (
          <Failed error={page.error} what="the audit trail" />
        ) : (page.data?.events.length ?? 0) === 0 ? (
          <Empty title="Nothing recorded" hint="Actions appear here as they happen." />
        ) : (
          <DataTable
            fill
            table="audit"
            rows={page.data?.events ?? []}
            rowKey={(event: AuditEntry, index) => `${event.at}-${index}`}
            columns={[
              {
                key: "at",
                header: "When",
                width: 190,
                cellClass: "font-mono",
                render: (e) => stamp(e.at),
              },
              { key: "actor", header: "Actor", width: 220, render: (e) => nameFor(e.actor) },
              {
                key: "action",
                header: "Action",
                width: 190,
                cellClass: "font-mono",
                render: (e) => e.action,
              },
              {
                key: "subject",
                header: "Subject",
                width: 260,
                cellClass: "font-mono",
                render: (e) =>
                  e.subject_type ? `${e.subject_type} ${e.subject_id ?? ""}`.trim() : "—",
              },
              {
                key: "ip",
                header: "From",
                width: 140,
                cellClass: "font-mono",
                render: (e) => e.source_ip ?? "—",
              },
              {
                key: "detail",
                header: "Detail",
                width: 340,
                render: (e) => (
                  <details>
                    <summary className="cursor-pointer font-mono text-[11px] text-dim">
                      detail
                    </summary>
                    <pre className="mt-1 overflow-x-auto font-mono text-[11px] text-dim">
                      {JSON.stringify(e.detail, null, 2)}
                    </pre>
                  </details>
                ),
              },
            ]}
          />
        )}
      </Panel>
    </Shell>
  );
}
